import React, { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { api } from '../../lib/api';

const PortalSetupPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';
  const [verifying, setVerifying] = useState(true);
  const [valid, setValid] = useState(false);
  const [clientName, setClientName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) { setVerifying(false); return; }
    api.clientAuth.verifyInvite(token)
      .then((data) => { setValid(true); setClientName(data.clientName); setEmail(data.email); })
      .catch((err) => setError(err.message || 'Invalid invitation'))
      .finally(() => setVerifying(false));
  }, [token]);

  const handleSubmit = async () => {
    if (!password || !confirm) { setError('Please fill in both fields.'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setLoading(true);
    setError('');
    try {
      await api.clientAuth.setupPassword(token, password);
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Failed to set password');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => { if (e.key === 'Enter') handleSubmit(); };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: '#F8FAFC',
      backgroundImage: 'radial-gradient(ellipse at 20% 0%, rgba(13,148,136,0.08) 0%, transparent 50%)',
      padding: '20px',
    }}>
      <div style={{
        width: '100%', maxWidth: '420px', background: 'white',
        borderRadius: '16px', padding: '40px 36px',
        boxShadow: '0 4px 24px rgba(0,0,0,0.06)', border: '1px solid #E2E8F0',
      }}>
        <div style={{ textAlign: 'center', marginBottom: '28px' }}>
          <img src="/logo.png" alt="VOP CRM" width={140} style={{ marginBottom: '16px' }} />
          <h1 style={{ margin: 0, fontSize: '22px', fontWeight: 700, color: '#1E293B' }}>Set Up Your Password</h1>
        </div>

        {verifying ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#64748B' }}>
            <Icon icon="solar:refresh-bold-duotone" width={32} style={{ animation: 'spin 1s linear infinite' }} />
            <p>Verifying invitation...</p>
          </div>
        ) : success ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '64px', height: '64px', borderRadius: '50%', margin: '0 auto 16px',
              background: 'rgba(16,185,129,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Icon icon="solar:check-circle-bold" width={36} style={{ color: '#10B981' }} />
            </div>
            <h2 style={{ color: '#1E293B', fontSize: '18px', margin: '0 0 8px' }}>Password Set Successfully!</h2>
            <p style={{ color: '#64748B', fontSize: '14px', margin: '0 0 24px' }}>You can now log in to your client portal.</p>
            <button onClick={() => navigate('/portal/login')} style={{
              width: '100%', padding: '12px', border: 'none', borderRadius: '10px', cursor: 'pointer',
              background: 'linear-gradient(135deg, #0D9488 0%, #0F766E 100%)',
              color: 'white', fontWeight: 600, fontSize: '15px', fontFamily: 'inherit',
            }}>
              Go to Login
            </button>
          </div>
        ) : !valid ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '64px', height: '64px', borderRadius: '50%', margin: '0 auto 16px',
              background: 'rgba(239,68,68,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Icon icon="solar:close-circle-bold" width={36} style={{ color: '#EF4444' }} />
            </div>
            <h2 style={{ color: '#1E293B', fontSize: '18px', margin: '0 0 8px' }}>Invalid Invitation</h2>
            <p style={{ color: '#64748B', fontSize: '14px', margin: '0 0 24px' }}>
              {error || 'This invitation link is invalid or has expired. Please contact your agent for a new invitation.'}
            </p>
            <Link to="/portal/login" style={{ color: '#0D9488', fontWeight: 600, textDecoration: 'none' }}>
              Go to Login
            </Link>
          </div>
        ) : (
          <>
            <p style={{ color: '#64748B', fontSize: '14px', textAlign: 'center', margin: '0 0 20px' }}>
              Welcome, <strong style={{ color: '#1E293B' }}>{clientName}</strong>! Set a password for <strong style={{ color: '#1E293B' }}>{email}</strong>.
            </p>

            {error && (
              <div style={{
                background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '8px',
                padding: '10px 14px', marginBottom: '16px', color: '#DC2626', fontSize: '13px',
                display: 'flex', alignItems: 'center', gap: '8px',
              }}>
                <Icon icon="solar:danger-circle-bold" width={16} />
                {error}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }} onKeyDown={handleKeyDown}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>New Password</label>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '10px', border: '1px solid #E2E8F0',
                  borderRadius: '10px', padding: '10px 14px', background: '#F8FAFC',
                }}>
                  <Icon icon="solar:lock-bold-duotone" width={18} style={{ color: '#94A3B8' }} />
                  <input type="password" placeholder="At least 8 characters" value={password} onChange={e => setPassword(e.target.value)}
                    style={{ flex: 1, border: 'none', outline: 'none', background: 'none', fontSize: '14px', color: '#1E293B', fontFamily: 'inherit' }} />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>Confirm Password</label>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '10px', border: '1px solid #E2E8F0',
                  borderRadius: '10px', padding: '10px 14px', background: '#F8FAFC',
                }}>
                  <Icon icon="solar:lock-check-bold-duotone" width={18} style={{ color: '#94A3B8' }} />
                  <input type="password" placeholder="Confirm your password" value={confirm} onChange={e => setConfirm(e.target.value)}
                    style={{ flex: 1, border: 'none', outline: 'none', background: 'none', fontSize: '14px', color: '#1E293B', fontFamily: 'inherit' }} />
                </div>
              </div>

              <button onClick={handleSubmit} disabled={loading} style={{
                width: '100%', padding: '12px', border: 'none', borderRadius: '10px', cursor: 'pointer',
                background: 'linear-gradient(135deg, #0D9488 0%, #0F766E 100%)',
                color: 'white', fontWeight: 600, fontSize: '15px', fontFamily: 'inherit',
                opacity: loading ? 0.7 : 1,
              }}>
                {loading ? 'Setting password...' : 'Set Password & Activate'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PortalSetupPassword;
